import { Fragment, useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Briefcase, Clock } from 'lucide-react';
import '@/assets/styles/components/application-review.styles.scss';
import VideoUpload from './_components/video-upload/VideoUpload.tsx';
import clsx from 'clsx';
import { ExpertApplicantDetailInfoResponse } from '@dopingtech/live_expert_applicant_bff_api_ts/api.ts';
import { UserType } from '@/constants';
import { WORK_STATUS_MAP } from '@/screens/application-onboarding/_components/onboarding-form/steps/work-info/work-info.constants.ts';

const APPLICATION_STATUS = {
	WAITING: 'İnceleniyor',
	REJECTED: 'Reddedildi',
	APPROVED: 'Onaylandı'
} as const;

type ApplicationReviewProps = ExpertApplicantDetailInfoResponse & {
	serviceTypeIdToName: { [key: number]: string };
	expertApplicantIdToName: { [key: number]: string };
	specialization: { [key: number]: string };
	educationLevels: Array<{
		id: number;
		name: string;
		description: string;
	}>;
	documentsContentType?: Array<{
		documentTypeId: number;
		documentTypeName: string;
		isUploaded: boolean;
	}>;
};

export const ApplicationReview = ({
	expertApplicant,
	serviceList,
	specialization,
	serviceTypeIdToName,
	expertApplicantIdToName,
	branchList,
	educationLevels,
	documentsContentType
}: ApplicationReviewProps) => {
	const serviceName = useMemo(
		() =>
			serviceList
				?.map(service => serviceTypeIdToName[service?.liveServiceTypeId])
				.filter(Boolean)
				.join(', '),
		[serviceList, serviceTypeIdToName]
	);
	const expertApplicantName = useMemo(
		() =>
			serviceList
				?.map(service => expertApplicantIdToName[service?.expertApplicantId])
				.filter(Boolean)
				.join(', '),
		[serviceList, expertApplicantIdToName]
	);
	const [isExpanded, setIsExpanded] = useState(false);
	const [isMobileExpanded, setIsMobileExpanded] = useState(false);

	const toggleMobileExpanded = () => {
		setIsMobileExpanded(!isMobileExpanded);
	};

	const hideMobileExpanded = () => {
		setIsMobileExpanded(false);
	};

	const renderPreferencesContent = () => {
		if (expertApplicant?.specializationType === UserType.UNIVERSITY_STUDENT) {
			return (
				<div className="student-preferences-content">
					<div className="stage-container">
						<h4>Kademe</h4>
						<p>Lise</p>
					</div>
					{branchList?.[0]?.lessonName && (
						<Fragment>
							<h4>Seçilen Dersler</h4>
							<div className="subjects-list">
								{branchList?.map(branch => (
									<span
										key={branch.branchId}
										className="subject-pill">
										{branch.lessonName}
									</span>
								))}
							</div>
						</Fragment>
					)}
				</div>
			);
		}

		return (
			<div className="">
				<h3 className="title">{isMobileExpanded && expertApplicant?.specializationType !== UserType.INSTRUCTOR && 'Seçilen'} Kademeler</h3>
				<div className="levels-row">
					{educationLevels.map(level => {
						const isSelected = branchList?.some(branch => branch.educationLevelId === level.id);
						const levelBranches = branchList?.filter(branch => branch.educationLevelId === level.id);

						if (isMobileExpanded && !isSelected) {
							return;
						}

						return (
							<div
								key={level.id}
								className="level-card">
								<div className={`level-checkbox ${isSelected ? 'checked' : ''}`}>
									{!isMobileExpanded && (
										<img
											src={isSelected ? 'assets/square-checked.svg' : 'assets/square-empty.svg'}
											alt="Square"
										/>
									)}
									<span>{level.name}</span>
								</div>
								<div className="subjects-section">
									{isSelected && expertApplicant?.specializationType === UserType.INSTRUCTOR && (
										<>
											<h4>Seçilen Dersler</h4>
											<div className="subjects-list">
												{levelBranches.map(branch => (
													<span
														key={branch.branchId}
														className="subject-pill">
														{branch.lessonName}
													</span>
												))}
											</div>
										</>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	};

	const status = useCallback(() => {
		return APPLICATION_STATUS[expertApplicant?.applicationStatus] || '';
	}, [expertApplicant]);

	const isNoVideoUploaded = (): boolean => {
		return !documentsContentType?.some(doc => doc.isUploaded === true) && !!expertApplicant;
	};

	return (
		<div className="application-review">
			{isNoVideoUploaded() && (
				<>
					<div className="application-header">
						<div className="success-icon">
							<img
								src="/assets/check.svg"
								alt="Check"
								className="check"
							/>
						</div>
						<div className="description">
							<h1>Başvurun başarılı şekilde gerçekleşti!</h1>
							<p>Başvuru durumunu bu sayfadan takip edebilirsin!</p>
						</div>
					</div>

					<div className="warning-banner">
						<span className="info-icon">i</span>
						<p>
							Başvurunun ön plana çıkması için, <strong>Kendini Tanıt</strong> ve <strong>Demo Video</strong> alanlarından istenilen video kayıtları gönder!
						</p>
					</div>
				</>
			)}
			{!!expertApplicant && (
				<div className="user-card">
					<div className="user-info-container">
						<div className="user-info">
							<img
								src={expertApplicant?.profilePicture || '/assets/avatar.svg'}
								alt="avatar"
								className="avatar"
							/>
							<div className="details">
								<div className="detail-wrapper">
									<div className="user-info-wrapper">
										<h2 className="name">{`${expertApplicant?.name} ${expertApplicant?.surname}`}</h2>
										<div className="role">{specialization?.description}</div>
									</div>
									<button
										className="inspect-button"
										onClick={toggleMobileExpanded}>
										İncele
										<i
											className={clsx('icons-alt-arrow-down', {
												rotated: isMobileExpanded
											})}
										/>
									</button>
								</div>
								<div
									className={clsx('status', {
										rejected: expertApplicant?.applicationStatus === 'REJECTED'
									})}>
									<Clock
										color={expertApplicant?.applicationStatus === 'REJECTED' ? 'var(--text-ff0000)' : '#F39C12'}
										size={18}
									/>
									<span>{status()}</span>
								</div>
							</div>
						</div>

						<div
							className={clsx('contact-info-container', {
								expanded: isMobileExpanded
							})}>
							<div className="contact-info">
								{isMobileExpanded && <h3 className="title">Kişisel Bilgilerim</h3>}
								<div className="info-item">
									<div className="icon">
										<i className="icons-letter" />
									</div>
									<span className="text">{expertApplicant?.email}</span>
								</div>

								{expertApplicant?.universityName && (
									<div className="info-item">
										<div className="icon">
											<i className="icons-cap-full" />
										</div>
										<span className="text">
											{expertApplicant?.universityName}
											<span className="subtitle">{expertApplicant?.departmentName}</span>
										</span>
									</div>
								)}

								<div className="info-item">
									<div className="icon">
										<i className="icons-smart-phone" />
									</div>
									<span className="text">{expertApplicant?.phone}</span>
								</div>

								{expertApplicant?.graduateUniversityName && (
									<div className="info-item">
										<div className="icon">
											<i className="icons-cap-full" />
										</div>
										<span className="text">
											{expertApplicant?.graduateUniversityName}
											<span className="subtitle">{expertApplicant?.graduateDepartmentName}</span>
										</span>
									</div>
								)}

								{!!expertApplicantName && (
									<div className="info-item">
										<div className="icon">
											<Briefcase size={14} />
										</div>
										<span className="text">{expertApplicantName}</span>
									</div>
								)}

								<div className="info-item">
									<div className="icon">
										<i className="icons-map-point" />
									</div>
									<span className="text">
										{expertApplicant?.cityName} / {expertApplicant?.districtName}
									</span>
								</div>

								{!!expertApplicant?.schoolType && (
									<div className="info-item">
										<div className="icon">
											<i className="icons-case-round" />
										</div>
										<span className="text">{WORK_STATUS_MAP[expertApplicant?.schoolType]}</span>
									</div>
								)}

								{!!serviceName && (
									<div className="info-item">
										<div className="icon">
											<i className="icons-pen" />
										</div>
										<span className="text">{serviceName}</span>
									</div>
								)}
							</div>
						</div>
					</div>
					{isMobileExpanded && (
						<>
							<div className="mobile-preferences-section">{renderPreferencesContent()}</div>
							<button
								className="hide-button"
								onClick={hideMobileExpanded}>
								Gizle
								<i className="icons-alt-arrow-up" />
							</button>
						</>
					)}

					<button
						className="preferences-button"
						onClick={() => setIsExpanded(!isExpanded)}>
						<span>Kademe / Branş Tercihlerim</span>
						{isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
					</button>

					<div
						className={clsx(`preferences-content ${expertApplicant?.specializationType}`, {
							expanded: isExpanded
						})}>
						{renderPreferencesContent()}
					</div>
				</div>
			)}

			<VideoUpload videoUploads={documentsContentType} />
		</div>
	);
};
